// src/components/exam/charts/SectionTrendCharts.jsx - IMPROVED VERSION

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

const SectionTrendCharts = ({ sectionMetrics, maxScore = 100 }) => {
  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (!sectionMetrics || Object.keys(sectionMetrics).length === 0) {
    return (
      <div className="w-full bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center gap-3 text-gray-600">
          <AlertCircle className="w-5 h-5" />
          <div>
            <p className="font-semibold">No Section Data Available</p>
            <p className="text-sm">Complete exams to see section trends</p>
          </div>
        </div>
      </div>
    );
  }

  // Get sections that have data
  const availableSections = Object.entries(sectionMetrics)
    .filter(([_, data]) => data.hasData)
    .map(([key, data]) => ({
      key,
      name: key.charAt(0).toUpperCase() + key.slice(1),
      data
    }));

  if (availableSections.length === 0) {
    return (
      <div className="w-full bg-white rounded-lg p-6 border border-gray-200">
        <p className="text-gray-600">No detailed section data available yet</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">📈 Section Performance Trends</h3>
        <p className="text-sm text-gray-600">Track your progress in each skill area</p>
      </div>

      {/* Grid of section charts - 2 per row on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {availableSections.map(({ key, name, data }) => (
          <div key={key} className="bg-white rounded-lg border border-gray-200 p-4">
            {/* Section Header with Trend */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">{name}</h4>
              <div className="flex items-center gap-2">
                {getTrendIcon(data.trend)}
                <span className="text-xs font-medium text-gray-600 capitalize">{data.trend}</span>
              </div>
            </div>

            {/* Mini Line Chart */}
            {data.chartData && data.chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={data.chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="examNumber"
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Exam', position: 'insideBottomRight', offset: -3, fontSize: 11 }}
                  />
                  <YAxis
  domain={[0, data.maxScore]}  // ✅ Use dynamic max score
  tick={{ fontSize: 12 }}
  label={{ value: `/${data.maxScore}`, angle: -90, position: 'insideLeft', fontSize: 12 }}
/>

                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        return (
                          <div className="bg-white p-2 border border-gray-200 rounded shadow-sm text-xs">
                            <p className="font-semibold">Exam #{payload[0].payload.examNumber}</p>
                            <p className="text-gray-600">{payload[0].payload.date}</p>
                            <p className="font-bold text-blue-600">
                              {payload[0].value}/{maxScore}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                    cursor={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#3b82f6"
                    dot={{ fill: '#3b82f6', r: 3 }}
                    activeDot={{ r: 5 }}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
                No trend data yet
              </div>
            )}

            {/* Stats Cards Below Chart */}
         <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-gray-100">
  <div className="text-center">
    <p className="text-xs text-gray-600 mb-1">Latest</p>
    <p className="text-lg font-bold text-gray-900">{data.lastScore}</p>
    <p className="text-xs text-gray-500">/{data.maxScore}</p>  {/* ✅ Dynamic max */}
  </div>
  <div className="text-center">
    <p className="text-xs text-gray-600 mb-1">Average</p>
    <p className="text-lg font-bold text-gray-900">{Math.round(data.average)}</p>
    <p className="text-xs text-gray-500">/{data.maxScore}</p>  {/* ✅ Dynamic max */}
  </div>
  <div className="text-center">
    <p className="text-xs text-gray-600 mb-1">Exams</p>
    <p className="text-lg font-bold text-gray-900">{data.chartData?.length || 0}</p>
    <p className="text-xs text-gray-500">tracked</p>
  </div>
</div>

          </div>
        ))}
      </div>

      {/* Summary Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          <span className="font-semibold text-blue-900">💡 Tip:</span> Each section is tracked independently. Focus on sections where your average is lower to improve overall performance.
        </p>
      </div>
    </div>
  );
};

export default SectionTrendCharts;
